import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, User, Wrench, BookOpen, TrendingUp } from 'lucide-react';
import { SectionHeading } from '../Reveal';
import { CAREER_CATEGORIES } from '../../data/content';

const CareerExplorer = () => {
  const [active, setActive] = useState(CAREER_CATEGORIES[0].id);
  const category = CAREER_CATEGORIES.find((c) => c.id === active);

  return (
    <section className="bg-navy py-24 md:py-32" data-testid="career-explorer-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          dark
          index="04"
          overline="Career Paths"
          title="Explore Career Paths"
          description="Thirteen fields. Dozens of careers. Pick a category and see what each career really involves — skills, courses, and where it can take you."
        />

        <div className="no-scrollbar mt-12 flex gap-2.5 overflow-x-auto pb-2" role="tablist" aria-label="Career categories">
          {CAREER_CATEGORIES.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={active === c.id}
              onClick={() => setActive(c.id)}
              data-testid={`category-pill-${c.id}`}
              className={`whitespace-nowrap rounded-full border px-5 py-2.5 text-sm font-semibold transition-[background-color,color,border-color] duration-200 ${
                active === c.id
                  ? 'border-coral bg-coral text-white'
                  : 'border-white/15 bg-white/5 text-white/70 hover:border-white/40 hover:text-white'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 grid gap-6 md:grid-cols-3"
          >
            {category.careers.map((career) => (
              <div
                key={career.name}
                className="flex flex-col rounded-3xl border border-white/10 bg-navy-light p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1.5 hover:border-coral/50"
                data-testid={`career-card-${career.name.toLowerCase().replace(/[\s/()]+/g, '-')}`}
              >
                <h3 className="font-display text-xl font-semibold text-white">{career.name}</h3>

                <div className="mt-5 space-y-4 text-sm">
                  <div>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-coral">
                      <User className="h-3.5 w-3.5" /> Suitable for
                    </p>
                    <p className="mt-1.5 leading-relaxed text-white/70">{career.suitableFor}</p>
                  </div>
                  <div>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-coral">
                      <Wrench className="h-3.5 w-3.5" /> Key skills
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {career.skills.map((s) => (
                        <span key={s} className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-coral">
                      <BookOpen className="h-3.5 w-3.5" /> Recommended courses
                    </p>
                    <ul className="mt-1.5 list-inside list-disc space-y-1 text-white/70 marker:text-coral">
                      {career.courses.map((c) => (
                        <li key={c}>{c}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-coral">
                      <TrendingUp className="h-3.5 w-3.5" /> Progression
                    </p>
                    <p className="mt-1.5 leading-relaxed text-white/70">{career.progression.join(' → ')}</p>
                  </div>
                </div>

                <Link
                  to="/career-roadmaps"
                  data-testid={`career-roadmap-cta-${career.name.toLowerCase().replace(/[\s/()]+/g, '-')}`}
                  className="mt-6 inline-flex items-center gap-2 pt-2 text-sm font-bold text-coral transition-colors hover:text-white"
                >
                  Explore Roadmap <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default CareerExplorer;
